/* =============================================================
   Cart drawer
   - Opens via window.WinonaCartDrawer.open() or [data-cart-toggle] click
   - Auto-opens on ?open-cart=1 (used by the /cart redirect to home)
   - Closes via overlay/close button/Escape
   - Re-renders via Shopify Section Rendering API after every AJAX
     cart mutation, so quantity/remove/add stay in sync without reload
   - Dispatches `cart:updated` so other components (header count,
     product section's CartState) can refresh
   ============================================================= */
(function () {
  'use strict';

  var SECTION_TYPE = 'cart-drawer';
  var QUERY_OPEN_KEY = 'open-cart';

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  function getRootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchCartJSON(url, opts) {
    opts = opts || {};
    return fetch(url, Object.assign({ credentials: 'same-origin' }, opts))
      .then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            var err = new Error(body && (body.description || body.message) || ('Cart request failed: ' + res.status));
            err.body = body;
            throw err;
          });
        }
        return res.json();
      });
  }

  /* ─────────────────────────────────────────────────────────────────
     Manager — one instance per page
     ───────────────────────────────────────────────────────────────── */
  function CartDrawer(root) {
    this.root = root;
    this.panel = $('[data-cart-panel]', root);
    this.sectionId = root.dataset.sectionId || SECTION_TYPE;
    this.lastFocus = null;
    this.isOpen = false;
    this.pendingRequests = 0;

    this.bind();
    this.maybeAutoOpenFromQuery();
  }

  CartDrawer.prototype.bind = function () {
    var self = this;

    /* Close triggers (overlay + close button) */
    $$('[data-cart-close]', this.root).forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        self.close();
      });
    });

    /* External open triggers anywhere on the page */
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-cart-toggle]');
      if (!trigger) return;
      event.preventDefault();
      self.open(trigger);
    });

    /* Keyboard: Escape closes, Tab is focus-trapped while open */
    document.addEventListener('keydown', function (event) {
      if (!self.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        self.close();
      } else if (event.key === 'Tab') {
        self.trapFocus(event);
      }
    });

    /* Delegate within drawer: qty/remove/toggle/checkout */
    this.root.addEventListener('click', function (event) {
      var qtyBtn = event.target.closest('[data-cart-qty-action]');
      var removeBtn = event.target.closest('[data-cart-remove]');
      var toggleBtn = event.target.closest('[data-cart-toggle-subscription]');
      var checkoutBtn = event.target.closest('[data-cart-checkout]');

      if (qtyBtn) {
        event.preventDefault();
        self.onQtyClick(qtyBtn);
      } else if (removeBtn) {
        event.preventDefault();
        self.onRemoveClick(removeBtn);
      } else if (toggleBtn) {
        event.preventDefault();
        self.onToggleSubscription(toggleBtn);
      } else if (checkoutBtn) {
        event.preventDefault();
        window.location.href = getRootUrl() + 'checkout';
      }
    });

    /* Re-render on external cart mutations (e.g. product page add-to-cart) */
    document.addEventListener('cart:updated', function (event) {
      var source = event && event.detail && event.detail.source;
      if (source === 'cart-drawer') return; /* avoid self-loop */
      self.refresh();
    });
  };

  CartDrawer.prototype.maybeAutoOpenFromQuery = function () {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get(QUERY_OPEN_KEY) === '1') {
        params.delete(QUERY_OPEN_KEY);
        var qs = params.toString();
        var clean = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        window.history.replaceState({}, '', clean);
        var self = this;
        /* Open on next tick so the rest of the page can mount first */
        window.requestAnimationFrame(function () { self.open(); });
      }
    } catch (e) { /* no-op */ }
  };

  /* ── Open / close ──────────────────────────────────────────────── */
  CartDrawer.prototype.open = function (returnFocusTo) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocus = returnFocusTo || document.activeElement;

    this.root.removeAttribute('inert');
    this.root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cart-drawer-locked');

    var self = this;
    /* Focus the close button after the slide-in starts */
    window.requestAnimationFrame(function () {
      var closeBtn = $('.cart-drawer__close', self.root);
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    });

    /* Always refresh on open so totals/items are current with the server.
       The server-rendered initial markup may be a few seconds stale. */
    this.refresh();
  };

  CartDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.setAttribute('aria-hidden', 'true');
    this.root.setAttribute('inert', '');
    document.documentElement.classList.remove('cart-drawer-locked');

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      try { this.lastFocus.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
    }
  };

  CartDrawer.prototype.trapFocus = function (event) {
    var focusables = $$([
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
    ].join(','), this.panel).filter(function (el) {
      return !el.hasAttribute('hidden') && el.offsetParent !== null;
    });
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ── Quantity / remove / toggle handlers ───────────────────────── */
  CartDrawer.prototype.onQtyClick = function (button) {
    if (button.disabled) return;
    var item = button.closest('[data-cart-item]');
    if (!item) return;
    var valueEl = $('[data-cart-qty-value]', item);
    if (!valueEl) return;

    var current = parseInt(valueEl.textContent, 10) || 1;
    var increase = button.dataset.cartQtyAction === 'increase';
    var next;
    if (increase) {
      /* Cap one-time lines at the configured limit. The Liquid markup
         also disables the button at the cap, but we guard here in case
         a fast double-click slips in before the section re-renders. */
      var qtyWrap = button.closest('[data-cart-qty]');
      var max = parseInt(qtyWrap && qtyWrap.dataset.otpQtyMax, 10);
      next = current + 1;
      if (max > 0 && next > max) next = max;
    } else {
      /* Minus floors at 1 — going to 0 is the trash icon's job. */
      next = Math.max(1, current - 1);
    }
    if (next === current) return;

    this.changeLine(item, next);
  };

  CartDrawer.prototype.onRemoveClick = function (button) {
    var item = button.closest('[data-cart-item]');
    if (!item) return;
    item.classList.add('is-removing');
    this.changeLine(item, 0);
  };

  /* ── OTP ↔ S&S toggle ──────────────────────────────────────────── */
  /* Mutate the line's selling_plan in place via /cart/change.js so the
     item keeps its position in the cart (a remove + re-add would push it
     to the end of the cart). The Ajax API requires the `line` (1-based
     position) — not the line key — when changing a selling plan, and a
     quantity must always be sent.
     https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js */
  CartDrawer.prototype.onToggleSubscription = function (button) {
    var self = this;
    var item = button.closest('[data-cart-item]');
    if (!item) return;

    var productId = item.dataset.productId;
    var variantId = item.dataset.variantId;
    var sellingPlanId = item.dataset.sellingPlanId;
    var currentlySubscription = item.dataset.subscription === 'true';

    /* Need a selling plan id to enable S&S. If absent we silently bail. */
    if (!currentlySubscription && !sellingPlanId) return;

    /* Going OTP → S&S: refuse if another line in the cart is already a
       subscription of the same product (changing the plan in place would
       let Shopify merge the two S&S lines). The Liquid markup already
       disables the toggle in this case; this is the belt-and-suspenders
       guard for paths that bypass the UI (devtools, programmatic clicks,
       stale DOM between a fast double-click and the section re-render). */
    if (!currentlySubscription && productId) {
      var conflict = self.root.querySelector(
        '[data-cart-item][data-product-id="' + productId + '"][data-subscription="true"]'
      );
      if (conflict) {
        console.warn('Subscribe toggle blocked: product already has a subscription line');
        return;
      }
    }

    /* Going S&S → OTP: refuse if the same variant already has an OTP line at
       the quantity cap. Dropping the selling plan lets Shopify merge this
       line into that OTP line (same variant), pushing it past the cap. The
       Liquid markup already disables the toggle in this case; this mirrors
       the OTP → S&S guard above for paths that bypass the UI. */
    if (currentlySubscription && variantId) {
      var otpLine = self.root.querySelector(
        '[data-cart-item][data-variant-id="' + variantId + '"][data-subscription="false"]'
      );
      if (otpLine) {
        var otpWrap = $('[data-cart-qty]', otpLine);
        var otpMax = parseInt(otpWrap && otpWrap.dataset.otpQtyMax, 10);
        var otpValueEl = $('[data-cart-qty-value]', otpLine);
        var otpQty = otpValueEl ? (parseInt(otpValueEl.textContent, 10) || 0) : 0;
        if (otpMax > 0 && otpQty >= otpMax) {
          console.warn('Subscribe toggle blocked: variant already has an OTP line at the quantity cap');
          return;
        }
      }
    }

    /* Line position (1-based) — required by change.js for selling_plan.
       Fall back to the DOM order if the data attribute is missing. */
    var lineNumber = parseInt(item.dataset.lineNumber, 10);
    if (!lineNumber) {
      var items = Array.prototype.slice.call(
        self.root.querySelectorAll('[data-cart-item]')
      );
      lineNumber = items.indexOf(item) + 1;
    }
    if (!lineNumber) return;

    var valueEl = $('[data-cart-qty-value]', item);
    var currentQty = valueEl ? (parseInt(valueEl.textContent, 10) || 1) : 1;

    /* S&S → OTP: keep current qty. OTP → S&S: force qty 1 (only one
       subscription allowed per product). selling_plan = null removes the
       plan; a plan id sets it. */
    var nextQty = currentlySubscription ? currentQty : 1;
    var nextSellingPlan = currentlySubscription ? null : parseInt(sellingPlanId, 10);

    button.disabled = true;
    item.classList.add('is-updating');
    this.beginRequest();

    fetchCartJSON(getRootUrl() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        line: lineNumber,
        quantity: nextQty,
        selling_plan: nextSellingPlan,
        sections: self.sectionId,
        sections_url: window.location.pathname
      })
    })
      .then(function (data) {
        self.applySections(data && data.sections);
      })
      .catch(function (err) {
        console.error('Cart drawer toggle failed', err);
        item.classList.remove('is-updating');
      })
      .then(function () {
        self.endRequest();
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'cart-drawer' }
        }));
      });
  };

  /* ── AJAX: /cart/change.js with section rendering ──────────────── */
  CartDrawer.prototype.changeLine = function (itemEl, newQty) {
    var self = this;
    var lineKey = itemEl.dataset.lineKey;
    if (!lineKey) return Promise.resolve();

    itemEl.classList.add('is-updating');
    this.beginRequest();

    return fetchCartJSON(getRootUrl() + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: newQty,
        sections: this.sectionId,
        sections_url: window.location.pathname
      })
    })
      .then(function (data) {
        self.applySections(data && data.sections);
      })
      .catch(function (err) {
        console.error('Cart drawer change failed', err);
        itemEl.classList.remove('is-updating', 'is-removing');
      })
      .then(function () {
        self.endRequest();
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'cart-drawer' }
        }));
      });
  };

  /* ── Refresh entire drawer from server (no mutation) ───────────── */
  CartDrawer.prototype.refresh = function () {
    var self = this;
    this.beginRequest();
    var url = getRootUrl() + '?sections=' + encodeURIComponent(this.sectionId);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data) return;
        /* The plain ?sections endpoint returns { 'cart-drawer': '<html>' } */
        self.applySections(data);
      })
      .catch(function (err) { console.warn('Cart drawer refresh failed', err); })
      .then(function () { self.endRequest(); });
  };

  /* ── Apply server-rendered sections to live DOM ────────────────── */
  CartDrawer.prototype.applySections = function (sections) {
    if (!sections) return;
    var html = sections[this.sectionId];
    if (!html) return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newRoot = doc.querySelector('[data-cart-drawer]');
    if (!newRoot) return;

    /* Swap each top-level data-cart-region target so we don't blow
       away the panel itself (and its transform / scroll state).

       Only the outermost regions are listed here — nested regions
       (e.g. items inside body, subtotal inside footer) come along
       with their parent. outerHTML is used so structural changes
       (the items region flipping between <ul> and <div> when the
       cart goes from populated → empty and back) work cleanly. */
    var regions = ['count', 'body', 'footer'];
    var liveRoot = this.root;
    regions.forEach(function (name) {
      var selector = '[data-cart-region="' + name + '"]';
      var live = liveRoot.querySelector(selector);
      var fresh = newRoot.querySelector(selector);
      if (!live || !fresh) return;
      live.outerHTML = fresh.outerHTML;
    });
  };

  CartDrawer.prototype.beginRequest = function () {
    this.pendingRequests += 1;
    this.root.setAttribute('data-loading', 'true');
  };
  CartDrawer.prototype.endRequest = function () {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) this.root.removeAttribute('data-loading');
  };

  /* ─────────────────────────────────────────────────────────────────
     Mount + public API
     ───────────────────────────────────────────────────────────────── */
  function mount() {
    var root = $('[data-cart-drawer]');
    if (!root) return null;
    if (root.dataset.cartDrawerInit === 'true') return window.WinonaCartDrawer;
    root.dataset.cartDrawerInit = 'true';
    var instance = new CartDrawer(root);

    window.WinonaCartDrawer = {
      open: function (trigger) { instance.open(trigger); },
      close: function () { instance.close(); },
      refresh: function () { return instance.refresh(); },
      isOpen: function () { return instance.isOpen; }
    };
    return window.WinonaCartDrawer;
  }

  if (document.readyState !== 'loading') mount();
  else document.addEventListener('DOMContentLoaded', mount);

  /* Re-mount when Theme Editor reloads the section */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.querySelector && target.querySelector('[data-cart-drawer]')) {
      var root = target.querySelector('[data-cart-drawer]');
      root.dataset.cartDrawerInit = '';
      mount();
    }
  });
})();
